import * as React from 'react';
import { Column } from 'src/components-v2/Layout';
import type { IPaginatedCard, TAlignment } from '../Paginator';

interface IImageCardsProps {
  cards: Array<IPaginatedCard> | Array<React.ReactElement<unknown>>;
  cardRef?: React.Ref<HTMLDivElement>;
  alignment?: TAlignment;
}

const ImageCards: React.FunctionComponent<IImageCardsProps> = ({
  cards,
  cardRef,
  alignment = 'Left',
}) => {
  const isCentered = alignment === 'Center';
  return (
    <>
      {cards?.map((card, i) => {
        const columns = getColumn(cards, i);
        return (
          <Column
            component='li'
            ref={cardRef}
            key={`card-column-${i}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              // Only declare textAlign when centered — the Left path must keep
              // inheriting so section-level CMS styles (textAlign in
              // cardsSection.styles) continue to cascade into cards.
              ...(isCentered && { textAlign: 'center' }),
              minWidth: {
                xs: 'calc(100% * 21 / var(--Grid-columns))',
                sm: '0',
              },
              '& a, & button': {
                alignSelf: isCentered ? 'center' : 'flex-start',
                mt: {
                  xs: '1.6rem',
                  sm: '0.4rem',
                  lg: '2.4rem',
                },
              },
              '& a:last-of-type, & button:last-of-type': {
                mt: 'auto',
              },
              '& p + p': {
                mt: {
                  xs: '0.8rem',
                  sm: '1.6rem',
                },
              },
              '& p:not(:empty):not(:last-child)': {
                mb: {
                  xs: '1.6rem',
                  sm: '0.4rem',
                  lg: '2.4rem',
                },
              },
              ...(isCentered && {
                '& .button-group': {
                  justifyContent: 'center',
                },
              }),
              ...getCardSx(cards, i),
              ...card.sx,
            }}
            {...columns}
          >
            {/* TODO: remove this when we remove the static payment and fraud-compliance pages: [redacted]##################################### */}
            {!card?.content && card}
            {card?.content && card?.content}
          </Column>
        );
      }) || null}
    </>
  );
};

export default ImageCards;

export const getColumn = (items: Array<unknown>, index: number) => {
  // 24 columns
  // offset 2 on either end
  // 20 workable columns, including spacing between cards

  // Note: spacing has not been blessed by design, but we've been using four cards
  // on the website for awhile and this is better than bleeding into the gutter.
  if (items.length === 4) {
    return {
      lgOffset: index === 0 ? 2 : 0.5,
      lg: 4.625,
      sm: 6,
      smOffset: 0,
      xs: 21,
      xsOffset: 1,
    };
  }
  if (items.length === 3) {
    return {
      lgOffset: index === 0 ? 2 : 0,
      lg: 6,
      sm: 7,
      smOffset: 0,
      xs: 21,
      xsOffset: 1,
    };
  }

  return {
    // two columns
    lgOffset: index === 0 ? 2 : 0,
    lg: 9,
    sm: 10,
    smOffset: index === 0 ? 1 : 0,
    xs: 21,
    xsOffset: 1,
  };
};

export const getCardSx = (items: Array<unknown>, index: number) => {
  const isLast = index === items.length - 1;
  if (items.length === 3) {
    return {
      flexGrow: { sm: 1 },
      ...(isLast && { mr: { lg: 'calc(100% * 2 / 24)' } }),
    };
  }
  if (items.length === 2) {
    return {
      flexGrow: { sm: 1 },
      ...(isLast && {
        mr: { sm: 'calc(100% * 1 / 24)', lg: 'calc(100% * 2 / 24)' },
      }),
    };
  }
  return {};
};
